import SafeUtil from '../../util/SafeUtil';
import BaseEntity from '../base/BaseEntity';
import PreviewConfig from './model/PreviewConfig';
import JsonUtil from '../../util/JsonUtil';
import ScanConfig from './model/ScanConfig';
import WatermarkConfig from './model/WatermarkConfig';

export default class Preference extends BaseEntity {
  // 获取当前登录者的信息
  static URL_API_PREFERENCE_FETCH = '/api/preference/fetch';
  static URL_API_SYSTEM_CLEANUP = '/api/preference/system/cleanup';
  static URL_API_PREFERENCE_EDIT_PREVIEW_CONFIG = '/api/preference/edit/preview/config';
  static URL_API_PREFERENCE_EDIT_SCAN = '/api/preference/edit/scan/config';
  static URL_API_PREFERENCE_SCAN_ONCE = '/api/preference/scan/once'; // 启动异步扫描，返回 taskId
  static URL_API_SCAN_PROGRESS = '/api/preference/scanProgress'; // 获取扫描进度

  static URL_API_UPDATE_VIRUS_DATABASE = '/api/preference/updateVirusDatabase';
  static URL_API_UPLOAD_VIRUS_DATABASE = '/api/preference/uploadVirusDatabase';
  // 系统升级接口
  static URL_API_UPGRADE_FRONTEND = '/api/preference/upgrade/frontend';
  static URL_API_UPGRADE_PC = '/api/preference/upgrade/pc';
  static URL_API_UPGRADE_MOBILE = '/api/preference/upgrade/mobile';
  static URL_API_UPGRADE_BACKUP = '/api/preference/upgrade/backup';
  static URL_API_UPGRADE_BACKUP_LIST = '/api/preference/upgrade/backup/list';
  static URL_API_UPGRADE_FULL = '/api/preference/upgrade/full';
  static URL_API_UPGRADE_STATUS = '/api/preference/upgrade/status';
  static URL_API_UPGRADE_LOG = '/api/preference/upgrade/log';
  // 水印配置接口
  static URL_API_PREFERENCE_EDIT_WATERMARK_CONFIG = '/api/preference/edit/watermark/config';
  static URL_API_PREFERENCE_WATERMARK_CONFIG = '/api/preference/watermark-config';

  // 文件同步配置接口
  static URL_API_PREFERENCE_EDIT_FILESYNC = '/api/preference/edit/filesync';
  static URL_API_PREFERENCE_FILESYNC_CONFIG = '/api/preference/filesync-config';

  // 网站名称
  name: string = '';
  logoUrl: string | null = null;
  faviconUrl: string | null = null;
  copyright: string = '';
  record: string = '';

  // 大小限制
  downloadDirMaxSize: number = -1;
  downloadDirMaxNum: number = -1;
  defaultTotalSizeLimit: number = -1;
  deletedKeepDays: number = 7;
  allowRegister: boolean = false;
  antivirusEnabled: boolean = false;
  virusScanTimeoutSeconds: number = 300;
  fullTextSearchEnabled: boolean = false;


  forbiddenExtensionsEnabled: boolean = false;

  // 预览配置
  previewConfig: PreviewConfig = new PreviewConfig();
  scanConfig: ScanConfig = new ScanConfig();
  watermarkConfig: WatermarkConfig = new WatermarkConfig();

  // 文件同步配置（JSON 字符串）
  filesSyncConfig: string = '{}';

  // 后台版本
  version: string | null = null;
  // 数据库类型（sqlite/mysql/...，可扩展）
  dbType: string | null = null;
  forbiddenKeywords: string | null = null;
  forbiddenExtensions: string | null = null;
  ctrlMod: number = 1;
  syncSet: number = 0;
  // 网站是否完成了安装
  installed: boolean = true;

  constructor(reactComponent?: React.Component) {
    super(reactComponent);
  }

  assign(obj: any) {
    super.assign(obj);

    this.assignEntity('previewConfig', PreviewConfig);
    this.assignEntity('scanConfig', ScanConfig);
    this.assignEntity('watermarkConfig', WatermarkConfig);

    if (obj.filesSyncConfig !== undefined) {
      this.filesSyncConfig = obj.filesSyncConfig;
    }
  }

  getTAG(): string {
    return 'preference';
  }

  getRecycleBinStatus() {
    return this.deletedKeepDays > 0;
  }

  getForm(): any {
    return {
      name: this.name,
      logoUrl: this.logoUrl,
      faviconUrl: this.faviconUrl,
      copyright: this.copyright,
      record: this.record,
      downloadDirMaxNum: this.downloadDirMaxNum,
      downloadDirMaxSize: this.downloadDirMaxSize,
      defaultTotalSizeLimit: this.defaultTotalSizeLimit,
      deletedKeepDays: this.deletedKeepDays,
      allowRegister: this.allowRegister,
      antivirusEnabled: this.antivirusEnabled,
      virusScanTimeoutSeconds: this.virusScanTimeoutSeconds,
      fullTextSearchEnabled: this.fullTextSearchEnabled,
      forbiddenKeywords: this.forbiddenKeywords,
      forbiddenExtensionsEnabled: this.forbiddenExtensionsEnabled,
      forbiddenExtensions: this.forbiddenExtensions,
      previewConfig: JsonUtil.toJson(this.previewConfig.getForm()),
      scanConfig: JsonUtil.toJson(this.scanConfig.getForm()),
      watermarkConfig: JsonUtil.toJson(this.watermarkConfig.getForm()),
    };
  }

  updateTitleAndFavicon() {
    if (this.faviconUrl) {
      let link: any =
        document.querySelector("link[rel*='icon']") ||
        document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = this.faviconUrl;
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    document.title = this.name;
  }

  httpFetch(successCallback?: any, errorCallback?: any, finalCallback?: any) {
    let that = this;
    this.httpPost(
      Preference.URL_API_PREFERENCE_FETCH,
      {},
      function (response: any) {
        that.assign(response.data.data);
        that.updateTitleAndFavicon();
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback,
      finalCallback
    );
  }

  httpSystemCleanup(
    password: string,
    successCallback?: any,
    errorCallback?: any,
    finalCallback?: any
  ) {
    let that = this;
    this.httpPost(
      Preference.URL_API_SYSTEM_CLEANUP,
      { password },
      function (response: any) {
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback,
      finalCallback
    );
  }

  httpSavePreviewEngine(
    successCallback?: any,
    errorCallback?: any,
    finalCallback?: any
  ) {
    const that = this;
    this.httpPost(
      Preference.URL_API_PREFERENCE_EDIT_PREVIEW_CONFIG,
      this.getForm(),
      function (response: any) {
        that.assign(response.data.data);
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback,
      finalCallback
    );
  }

  httpSaveScan(
    successCallback?: any,
    errorCallback?: any,
    finalCallback?: any
  ) {
    const that = this;
    const scanConfigData = this.scanConfig.getForm();
    if (!scanConfigData.autoCleanEnabled) {
      scanConfigData.autoCleanDays = null;
    }
    const params = {
      ...this.getForm(),
      scanConfig: JsonUtil.toJson(scanConfigData)
    };
    that.loading = true;

    this.httpPost(
      Preference.URL_API_PREFERENCE_EDIT_SCAN,
      params,
      function (response: any) {
        that.loading = false;
        that.assign(response.data.data);
        SafeUtil.safeCallback(successCallback)(response);
      },
      function (error: any) {
        that.loading = false;
        SafeUtil.safeCallback(errorCallback)(error);
      },
      finalCallback
    );
  }

  httpSaveWatermarkConfig(
    watermarkConfig: any,
    successCallback?: any,
    errorCallback?: any,
    finalCallback?: any
  ) {
    const that = this;
    const watermarkConfigJson = JSON.stringify(watermarkConfig);
    const params = {
      watermarkConfig: watermarkConfigJson,
    };

    console.log('发送的参数对象:', params);

    this.httpPost(
      Preference.URL_API_PREFERENCE_EDIT_WATERMARK_CONFIG,
      params,
      function (response: any) {
        console.log('水印配置保存成功:', response);
        that.assign(response.data.data);
        SafeUtil.safeCallback(successCallback)(response);
      },
      function (error: any) {
        console.error('水印配置保存失败:', error);
        SafeUtil.safeCallback(errorCallback)(error);
      },
      finalCallback
    );
  }

  httpFetchWatermarkConfig(
    successCallback?: any,
    errorCallback?: any,
    finalCallback?: any
  ) {
    this.httpGet(
      Preference.URL_API_PREFERENCE_WATERMARK_CONFIG,
      {},
      function (response: any) {
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback,
      finalCallback
    );
  }

  httpFetchFileSyncConfig(
    successCallback?: any,
    errorCallback?: any,
    finalCallback?: any
  ) {
    this.httpGet(
      Preference.URL_API_PREFERENCE_FILESYNC_CONFIG,
      {},
      function (response: any) {
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback,
      finalCallback
    );
  }

  updateSyncConfig(
    syncConfig: Record<string, any>,
    successCallback?: (response: any) => void,
    errorCallback?: (error: any) => void,
    finalCallback?: () => void
  ) {
    const that = this;
    const filesyncConfigJson = JSON.stringify(syncConfig);
    const params = {
      filesyncConfig: filesyncConfigJson,
    };

    console.log('发送的文件同步配置参数:', params);

    this.httpPost(
      Preference.URL_API_PREFERENCE_EDIT_FILESYNC,
      params,
      function (response: any) {
        console.log('文件同步配置保存成功:', response);
        that.assign(response.data.data);
        SafeUtil.safeCallback(successCallback)(response);
      },
      function (error: any) {
        console.error('文件同步配置保存失败:', error);
        SafeUtil.safeCallback(errorCallback)(error);
      },
      finalCallback
    );
  }

  /**
   * 启动异步扫描（兼容旧版调用）
   */
  httpScanOnce(
    successCallback?: (response: any) => void,
    errorCallback?: (error: any) => void,
    finalCallback?: any
  ) {
    this.httpStartScan(successCallback, errorCallback, finalCallback);
  }

  /**
   * 启动异步扫描
   */
  httpStartScan(
    successCallback?: (response: any) => void,
    errorCallback?: (error: any) => void,
    finalCallback?: any
  ) {
    this.httpPost(
      Preference.URL_API_PREFERENCE_SCAN_ONCE,
      {},
      function (response: any) {
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback,
      finalCallback
    );
  }

  /**
   * 获取扫描进度
   * @param taskId 任务ID
   */
  httpGetScanProgress(
    taskId: string,
    successCallback?: (response: any) => void,
    errorCallback?: (error: any) => void,
    finalCallback?: any
  ) {
    this.httpGet(
      `${Preference.URL_API_SCAN_PROGRESS}?taskId=${taskId}`,
      {},
      function (response: any) {
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback,
      finalCallback
    );
  }

// 在 Preference 类中添加文件上传方法
httpUpload(url: string, formData: FormData, successCallback?: any, errorCallback?: any, finalCallback?: any) {
  // 使用 fetch 直接发送，credentials: 'include' 会自动携带 cookie
  fetch(url, {
      method: 'POST',
      body: formData,
      credentials: 'include'   // 确保携带 session cookie
  })
  .then(response => response.json())
  .then(data => {
      if (data.code === 0 || data.code === 'OK') {
          SafeUtil.safeCallback(successCallback)(data);
      } else {
          SafeUtil.safeCallback(errorCallback)(data);
      }
  })
  .catch(error => {
      SafeUtil.safeCallback(errorCallback)(error);
  })
  .finally(() => {
      SafeUtil.safeCallback(finalCallback)();
  });
}
}